/**
 * Number formatting utilities following Apple's Human Interface Guidelines
 * Optimized for dashboard KPIs and large number display
 * 
 * ⚠️ DEPRECATED: This file is being phased out in favor of useTenantConfig hook
 * which provides dynamic formatting based on tenant configuration.
 * 
 * New code should use:
 * - useTenantConfig().formatCurrency()
 * - useTenantConfig().formatNumber()
 * - useTenantConfig().formatPercentage()
 */

export interface NumberFormatOptions {
  locale?: string;
  currency?: string;
  precision?: number;
  compact?: boolean;
  showSign?: boolean;
  maxCompactDigits?: number;
}

/**
 * Format numbers according to Apple's design guidelines
 * - Uses appropriate abbreviations (K, M, B) for large numbers
 * - Maintains precision contextually
 * - Supports localization
 * - Optimized for readability and accessibility
 */
export function formatNumber(
  value: number, 
  options: NumberFormatOptions = {}
): string {
  const {
    locale = 'es-GT',
    precision = 2,
    compact = true,
    showSign = false,
    maxCompactDigits = 6
  } = options;

  // Handle edge cases
  if (isNaN(value) || !isFinite(value)) return '0';
  
  const absoluteValue = Math.abs(value);
  const isNegative = value < 0;
  const sign = isNegative ? '-' : (showSign && value > 0 ? '+' : '');

  // For very small numbers, show more precision
  if (absoluteValue < 1 && absoluteValue > 0) {
    return `${sign}${absoluteValue.toFixed(Math.max(precision, 3))}`;
  }

  // Use compact notation for large numbers
  if (compact && absoluteValue >= Math.pow(10, maxCompactDigits)) {
    // For extremely large numbers, use scientific notation
    if (absoluteValue >= 1e12) {
      return `${sign}${(absoluteValue / 1e12).toFixed(1)}T`;
    }
    if (absoluteValue >= 1e9) {
      return `${sign}${(absoluteValue / 1e9).toFixed(1)}B`;
    }
    if (absoluteValue >= 1e6) {
      return `${sign}${(absoluteValue / 1e6).toFixed(1)}M`;
    }
    if (absoluteValue >= 1e3) {
      return `${sign}${(absoluteValue / 1e3).toFixed(1)}K`;
    }
  }

  // For regular numbers, use locale-appropriate formatting
  try {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
    }).format(absoluteValue);
    
    return `${sign}${formatted}`;
  } catch (error) {
    // Fallback to basic formatting if Intl is not supported
    return `${sign}${absoluteValue.toFixed(precision)}`;
  }
}

/**
 * Format currency values with Apple's design principles
 * - Contextual precision (fewer decimals for large amounts)
 * - Compact notation for very large amounts
 * - Proper currency symbol placement
 */
export function formatCurrency(
  value: number,
  options: NumberFormatOptions & { currency?: string } = {}
): string {
  const {
    locale = 'es-GT',
    currency = 'GTQ',
    compact = true,
    maxCompactDigits = 6
  } = options;

  const absoluteValue = Math.abs(value);
  const isNegative = value < 0;

  // For very large amounts, use compact notation with currency
  if (compact && absoluteValue >= Math.pow(10, maxCompactDigits)) {
    const sign = isNegative ? '-' : '';
    const currencySymbol = getCurrencySymbol(currency, locale);
    
    if (absoluteValue >= 1e9) {
      return `${sign}${currencySymbol}${(absoluteValue / 1e9).toFixed(1)}B`;
    }
    if (absoluteValue >= 1e6) {
      return `${sign}${currencySymbol}${(absoluteValue / 1e6).toFixed(1)}M`;
    }
    if (absoluteValue >= 1e3) {
      return `${sign}${currencySymbol}${(absoluteValue / 1e3).toFixed(1)}K`;
    }
  }

  // Use standard currency formatting for smaller amounts
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: absoluteValue >= 1000 ? 0 : 2,
      maximumFractionDigits: absoluteValue >= 1000 ? 2 : 2,
    }).format(value);
  } catch (error) {
    // Fallback formatting
    const symbol = getCurrencySymbol(currency, locale);
    const formatted = formatNumber(value, { ...options, compact: false });
    return `${symbol}${formatted}`;
  }
}

/**
 * Format percentage values
 */
export function formatPercentage(
  value: number,
  options: NumberFormatOptions = {}
): string {
  const {
    locale = 'es-GT',
    precision = 1,
    showSign = true
  } = options;

  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
      signDisplay: showSign ? 'exceptZero' : 'auto'
    }).format(value / 100);
    
    return formatted;
  } catch (error) {
    const sign = showSign && value > 0 ? '+' : '';
    return `${sign}${value.toFixed(precision)}%`;
  }
}

/**
 * Get currency symbol for a given currency code
 */
function getCurrencySymbol(currency: string, locale: string): string {
  const symbols: Record<string, string> = {
    'GTQ': 'Q',
    'USD': '$',
    'EUR': '€',
    'MXN': '$',
    'HNL': 'L',
    'CRC': '₡'
  };
  
  return symbols[currency] || currency;
}

/**
 * Smart number formatting that adapts based on context
 * Perfect for KPI cards and dashboard metrics
 */
export function formatKPIValue(
  value: number,
  type: 'currency' | 'number' | 'percentage' = 'number',
  options: NumberFormatOptions = {}
): string {
  const defaultOptions = {
    compact: true,
    maxCompactDigits: 5, // Show compact notation sooner for KPIs
    ...options
  };

  switch (type) {
    case 'currency':
      return formatCurrency(value, defaultOptions);
    case 'percentage':
      return formatPercentage(value, defaultOptions);
    default:
      return formatNumber(value, defaultOptions);
  }
}

/**
 * Format quantity values with Apple's design principles
 * - Always shows 4 decimal places for DigiFact compliance
 * - Removes trailing zeros for better readability when appropriate
 */
export function formatQuantity(
  value: number,
  options: NumberFormatOptions & { showAllDecimals?: boolean } = {}
): string {
  const {
    locale = 'es-GT',
    showAllDecimals = false
  } = options;

  // Handle edge cases
  if (isNaN(value) || !isFinite(value)) return '0.0000';
  
  try {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: showAllDecimals ? 4 : 0,
      maximumFractionDigits: 4,
    }).format(value);
    
    return formatted;
  } catch (error) {
    // Fallback to basic formatting if Intl is not supported
    return value.toFixed(4);
  }
}

/**
 * Format quantity values specifically for DigiFact XML (always 4 decimals)
 */
export function formatQuantityForDigiFact(value: number): string {
  if (isNaN(value) || !isFinite(value)) return '0.0000';
  return value.toFixed(4);
}

/**
 * Get readable number for accessibility (screen readers)
 */
export function getAccessibleNumber(value: number, type: 'currency' | 'number' | 'percentage' = 'number'): string {
  const absoluteValue = Math.abs(value);
  
  if (type === 'currency') {
    if (absoluteValue >= 1e9) return `${value / 1e9} billones`;
    if (absoluteValue >= 1e6) return `${value / 1e6} millones`;
    if (absoluteValue >= 1e3) return `${value / 1e3} mil`;
    return `${value}`;
  }
  
  if (type === 'percentage') {
    return `${value} por ciento`;
  }
  
  if (absoluteValue >= 1e9) return `${value / 1e9} billones`;
  if (absoluteValue >= 1e6) return `${value / 1e6} millones`;
  if (absoluteValue >= 1e3) return `${value / 1e3} mil`;
  return `${value}`;
}